"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import HoverArrow from "@/components/shared/HoverArrow";
import AnswerBox from "@/components/shared/AnswerBox";
import { faqCategories } from "@/lib/faq";
import { PageHeader } from "@/components/shared/PageHeader";
import { CTABanner } from "@/components/shared/CTABanner";
import { EASE } from "@/lib/motion";

const categories = faqCategories;

function FAQItem({
  question,
  answer,
  index,
}: {
  question: string;
  answer: string;
  index: number;
}) {
  return (
    // CSS entrance (.enter) – visible before hydration, and re-runs on remount
    // when the category tab changes, like the previous framer-motion version.
    <div
      className="enter [--enter-y:16px] [animation-duration:0.4s] border-t border-line py-6"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <h3 className="font-display font-semibold text-lg text-ink mb-3">
        {question}
      </h3>
      <div className="pr-10 text-ink-soft leading-relaxed space-y-3">
        {answer.split("\n\n").map((para, i) => (
          <p key={i} className="whitespace-pre-line">{para}</p>
        ))}
      </div>
    </div>
  );
}

export default function FAQPageContent() {
  const [activeCategory, setActiveCategory] = useState("abq");

  // Deep-Links wie /faq/#adh (z. B. aus dem Ratgeber-Menü) öffnen die Kategorie direkt
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash && categories.some((cat) => cat.id === hash)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setActiveCategory(hash);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  return (
    <>
      <PageHeader
        divider={false}
        eyebrow="FAQ"
        title={
          <>
            Deine Fragen, unsere <span className="text-accent">Antworten</span>
          </>
        }
        subtitle="Alle Infos zu Abiturbestenquote, AdH, ZEQ, Berechnungen, Diensten und Ausbildungen. Klar und verständlich erklärt."
      />

      {/* ── AEO answer box: sichtbare Kurzantwort direkt unter dem Header ── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-2">
        <AnswerBox
          question="Wie funktioniert die Medizin-Zulassung in Deutschland?"
          answer="Hier findest du Antworten auf die häufigsten Fragen zur Medizin-Zulassung in Deutschland und zur ncrechner App – von den drei Vergabequoten über die Anrechnung von Diensten und Ausbildungen bis zur Berechnung deiner Chancen. Alle Zulassungswerte basieren auf den offiziellen Daten von hochschulstart.de."
          keyTakeaways={[
            "Studienplätze werden über drei Quoten vergeben: Abiturbestenquote (ABQ), Auswahlverfahren der Hochschulen (AdH) und zusätzliche Eignungsquote (ZEQ).",
            "Dienste, Ausbildungen und Wartezeit können deine Chancen in einzelnen Quoten verbessern.",
            "ncrechner berechnet deine Zulassungschancen aus deinen Werten – kostenlos und in Echtzeit.",
          ]}
          audience="Für Studieninteressierte, die sich für Human-, Zahn-, Tier- oder Veterinärmedizin bewerben und das Vergabeverfahren verstehen möchten."
        />
      </div>

      {/* ── Category tabs ── */}
      <div className="sticky top-16 z-30 bg-paper/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={cn(
                  "w-full px-2 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors duration-200 text-center leading-tight cursor-pointer",
                  activeCategory === cat.id
                    ? "bg-ink text-paper"
                    : "text-ink-soft hover:bg-paper-deep hover:text-ink"
                )}
              >
                {cat.short}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ content ── */}
      <section className="bg-paper py-16 lg:py-20 min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Every category is rendered into the DOM and only toggled with the
              `hidden` attribute, so all FAQ answers are present in the
              server-rendered HTML (required for the FAQ rich result) while the
              tabs still switch categories client-side. */}
          {categories.map((cat) => (
            <div key={cat.id} hidden={activeCategory !== cat.id}>
              {/* Category header */}
              <div className="mb-10">
                <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint mb-3">
                  <span className="w-1.5 h-1.5 bg-accent shrink-0" aria-hidden="true" />
                  {cat.short}
                </p>
                <h2 className="font-display font-bold text-3xl text-ink tracking-tight">
                  {cat.label}
                </h2>
                <p className="text-ink-faint text-sm mt-2 tnum">
                  {cat.faqs.length} Frage{cat.faqs.length !== 1 ? "n" : ""} in dieser Kategorie
                </p>
              </div>

              {/* FAQ items */}
              <div>
                {cat.faqs.map((faq, i) => (
                  <FAQItem
                    key={`${cat.id}-${i}`}
                    question={faq.question}
                    answer={faq.answer}
                    index={i}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Still questions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mt-16 border border-line rounded-2xl bg-paper-deep p-8 sm:p-10"
          >
            <h3 className="font-display font-bold text-xl text-ink mb-2">
              Deine Frage ist noch offen?
            </h3>
            <p className="text-ink-soft text-sm leading-relaxed mb-3 max-w-lg">
              Schreib uns einfach eine E-Mail. Wir helfen dir gerne weiter und
              beantworten alle Fragen rund um das Bewerbungsverfahren.
            </p>
            <p className="text-ink-soft text-xs leading-relaxed mb-5 max-w-lg">
              <span className="inline-block mb-3">Unsere Zulassungswerte basieren auf den offiziellen Daten von{" "}
              <a 
                href="https://www.hochschulstart.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent hover:underline font-semibold"
              >
                hochschulstart.de
              </a>
              .</span>
            </p>
            <a
              href="mailto:info@ncrechner.de"
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink text-paper font-semibold text-sm hover:bg-accent transition-colors duration-200 cursor-pointer"
            >
              info@ncrechner.de
              <HoverArrow />
            </a>
          </motion.div>
        </div>
      </section>

      <CTABanner
        title="Berechne deine Chancen."
        text="Lade ncrechner kostenlos herunter und finde in wenigen Minuten heraus, an welchen Hochschulen du Chancen hast."
      />
    </>
  );
}
